// src/components/MessagesClient.tsx
import { useState, useMemo, useEffect, useRef } from "react";
import {
  DEFAULT_THEME,
  PRESET_THEMES as THEME_PRESETS,
  type ThemeTokens,
} from "@theme/tokens";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Video, Plus, Send, Paintbrush } from "lucide-react";
import { ThemeScope } from "@theme/ThemeScope";
import { useResolvedTheme } from "@theme/useResolvedTheme";
import { useSocket } from "@/hooks/useSocket";
import { fetchWithAuth } from '../../lib/fetchWithAuth';
import {
  ConversationRenamedListener,
  type RenameUpdate,
} from "./ConversationRenamedListener";
import {
  ConversationThemeListener,
  type ThemeUpdate,
} from "./ConversationThemeListener";

type PresetKey = keyof typeof THEME_PRESETS;

type Participant = {
  id: string;
  username: string;
  displayName?: string | null;
};

type Conversation = {
  id: string;
  type: "dm" | "group";
  name?: string | null;
  creatorId?: string | null; // admin = creator for groups
  participants: Participant[]; // includes current user
  themePresetKey?: PresetKey | null;
  themeOverrides?: Partial<ThemeTokens> | null;
};

type Msg = { id: string; from: string; text: string; at: string };

type Props = {
  currentUserId: string;
  currentUserName?: string;
  conversation: Conversation;
  initialMessages?: Msg[];
};

// Narrow to a plain JSON object
function isJsonObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

// Ensure a string is an actual preset key
function isPresetKey(x: unknown): x is PresetKey {
  return typeof x === "string" && x in THEME_PRESETS;
}

export default function MessagesClient({
  currentUserId,
  currentUserName,
  conversation,
  initialMessages = [],
}: Props) {
  /** socket **/
  const { socket, connected } = useSocket({ joinThreadId: conversation.id });

  /** theme: source-of-truth from conversation, with local edits for preview **/
  const [presetKey, setPresetKey] = useState<PresetKey>(
    conversation.themePresetKey ?? "ocean-turtle",
  );
  const [overrides, setOverrides] = useState<Partial<ThemeTokens>>(
    conversation.themeOverrides ?? {},
  );
  const [panelOpen, setPanelOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Who can edit?
  const isDM = conversation.type === "dm" && conversation.participants.length === 2;
  const isGroup = conversation.type === "group";
  const isAdmin = isGroup ? conversation.creatorId === currentUserId : false;
  const canEditTheme = isDM || isAdmin;

  // Compute initial title based on conversation type
  const computedInitialTitle = useMemo(() => {
    if (isGroup) {
      if (conversation.name && conversation.name.trim()) return conversation.name;
      return conversation.participants.map((p) => p.username).join(", ");
    } else {
      const other =
        conversation.participants.find((p) => p.id !== currentUserId) ??
        conversation.participants[0];
      return other?.displayName || other?.username || "Direct Message";
    }
  }, [conversation, currentUserId, isGroup]);

  // Local title state for live updates from socket
  const [liveTitle, setLiveTitle] = useState(computedInitialTitle);

  useEffect(() => {
    // Keep in sync if route changes to another conversation
    setLiveTitle(computedInitialTitle);
  }, [computedInitialTitle, conversation.id]);

  // Listen for customizer open event from settings
  useEffect(() => {
    const handleOpenCustomizer = () => setPanelOpen(true);
    window.addEventListener('openMessagesCustomizer', handleOpenCustomizer);
    return () => {
      window.removeEventListener('openMessagesCustomizer', handleOpenCustomizer);
    };
  }, []);

  // Build a full ThemeTokens object (preset/default + partial overrides)
  const communityTokens: ThemeTokens = useMemo(() => {
    const base: ThemeTokens = THEME_PRESETS[presetKey] ?? DEFAULT_THEME;
    return { ...base, ...overrides };
  }, [presetKey, overrides]);

  const resolvedTheme = useResolvedTheme({
    presetKey,
    community: communityTokens,
  });

  /** messages state + wiring **/
  const [messages, setMessages] = useState<Msg[]>(initialMessages);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [draft, setDraft] = useState("");

  // Fetch messages from backend
  useEffect(() => {
    async function fetchMessages() {
      try {
        const res = await fetchWithAuth(`/api/conversations/${conversation.id}/messages`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
        }
      } catch (error) {
      }
    }
    fetchMessages();
  }, [conversation.id]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!socket) return;
    const onNew = (msg: Msg) => setMessages((m) => [...m, msg]);
    socket.on("message:new", onNew);
    return () => {
      socket.off("message:new", onNew);
      socket.emit("leave", conversation.id);
    };
  }, [socket, conversation.id]);

  async function handleSend() {
    const text = draft.trim();
    if (!text) return;
    const optimistic: Msg = {
      id: crypto.randomUUID(),
      from: currentUserId,
      text,
      at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    socket?.emit("message:send", { threadId: conversation.id, text, userId: parseInt(currentUserId) });
  }

  async function saveTheme() {
    setSaving(true);
    const res = await fetch(`/conversations/${conversation.id}/theme`, {
      method: "POST",
      body: JSON.stringify({ presetKey, themeOverrides: overrides }), // <-- send themeOverrides
    });
    setSaving(false);
    if (!res.ok) {
      alert("Failed to save theme");
      return;
    }
    setPanelOpen(false);
  }

  /** UI **/
  return (
    <ThemeScope theme={resolvedTheme} id={`conv-${conversation.id}`}>
      <div className="messages-page">
        {/* Socket push listeners */}
        <ConversationRenamedListener
          conversationId={conversation.id}
          onRename={(u: RenameUpdate) => {
            setLiveTitle(u.name ?? "Untitled conversation");
          }}
        />
        <ConversationThemeListener
          conversationId={conversation.id}
          onTheme={(u: ThemeUpdate) => {
            setPresetKey(isPresetKey(u.themePresetKey) ? u.themePresetKey : "ocean-turtle");
            const nextOverrides = isJsonObject(u.themeOverrides)
              ? (u.themeOverrides as Partial<ThemeTokens>)
              : {};
            setOverrides(nextOverrides);
          }}
        />

        {/* Sidebar */}
        <aside className="messages-sidebar">
          <div className="animated-bg h-28 p-4 flex items-end">
            <div>
              <h2 className="text-lg font-semibold" style={{ color: "var(--text-color)" }}>
                {liveTitle}
              </h2>
              <p className="text-xs opacity-80" style={{ color: "var(--text-color)" }}>
                {conversation.participants.length} {isGroup ? "participants" : "participants"}
              </p>
            </div>
          </div>

          <div className="p-3" style={{ color: "var(--text-color)" }}>
            <div className="flex items-center gap-2 mb-3">
              <button className="icon-btn" title="Members" aria-label="Members" style={{ borderColor: "var(--accent)" }}>
                <Users size={16} />
              </button>
              <button className="icon-btn" title="Start call" aria-label="Start call" style={{ borderColor: "var(--accent)" }}>
                <Video size={16} />
              </button>

              {canEditTheme && (
                <button
                  className="icon-btn"
                  title="Theme"
                  aria-label="Theme"
                  onClick={() => setPanelOpen((v) => !v)}
                  style={{ borderColor: "var(--accent)" }}
                >
                  <Paintbrush size={16} />
                </button>
              )}

              <button
                className="icon-btn"
                title="New"
                aria-label="New"
                onClick={() => alert("New conversation…")}
                style={{ borderColor: "var(--accent)" }}
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Conversation quick access removed - use main messages page */}
          </div>
        </aside>

        {/* Chat area */}
        <section className="chat-area">
          {/* Header */}
          <div className="p-3 flex items-center justify-between" style={{ color: "var(--text-color)" }}>
            <div className="flex items-center gap-2">
              <div className="size-8 rounded-full" style={{ background: "var(--card-bg)", border: `1px solid var(--accent)` }} />
              <div>
                <div className="text-sm font-semibold">{liveTitle}</div>
                <div className="text-xs opacity-80">
                  {isGroup ? `${conversation.participants.length} members` : "Direct Message"}
                  {!connected && <span className="ml-2 opacity-70">(connecting…)</span>}
                </div>
              </div>
            </div>

            {canEditTheme && (
              <button
                className="icon-btn"
                onClick={() => setPanelOpen((v) => !v)}
                style={{ borderColor: "var(--accent)" }}
                aria-label="Edit theme"
                title="Edit theme"
              >
                <Paintbrush size={16} />
              </button>
            )}
          </div>

          {/* Messages */}
          <ul ref={listRef} className="chat-messages">
            <AnimatePresence initial={false}>
              {messages.map((m) => (
                <motion.li
                  key={m.id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ type: "spring", stiffness: 500, damping: 40 }}
                  className="rounded-xl p-3"
                  style={{
                    background: "var(--card-bg)",
                    border: `1px solid var(--accent)`,
                    color: "var(--text-color)",
                  }}
                >
                  <div className="text-xs opacity-80">
                    {renderSender(m.from, conversation.participants, currentUserId, currentUserName)} •{" "}
                    {new Date(m.at).toLocaleTimeString()}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap">{m.text}</div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>

          {/* Composer */}
          <div className="p-3 flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={connected ? "Message…" : "Connecting…"}
              className="flex-1 rounded-lg px-3 py-2 outline-none"
              style={{
                backgroundColor: "rgba(255,255,255,0.10)",
                color: "var(--text-color)",
                border: `1px solid var(--accent)`,
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              onClick={handleSend}
              className="rounded-lg px-3 py-2 flex items-center gap-2"
              style={{ background: "var(--accent)", color: "#000", border: 0 }}
              aria-label="Send"
            >
              <Send size={16} />
              <span className="text-sm font-medium">Send</span>
            </button>
          </div>

          {/* Inline theme panel (admin or DM) */}
          {panelOpen && canEditTheme && (
            <div className="p-3" style={{ borderTop: "1px solid var(--accent)" }}>
              <div className="grid md:grid-cols-2 gap-4">
                <div
                  className="rounded-xl border p-4"
                  style={{ background: "var(--card-bg)", borderColor: "var(--accent)" }}
                >
                  <label className="block text-sm mb-2" style={{ color: "var(--text-color)" }}>
                    Preset
                  </label>
                  <select
                    className="w-full rounded-lg px-3 py-2 bg-transparent"
                    value={presetKey}
                    onChange={(e) => setPresetKey(e.target.value as PresetKey)}
                    style={{ color: "var(--text-color)", border: `1px solid var(--accent)` }}
                  >
                    {(Object.keys(THEME_PRESETS) as PresetKey[]).map((k) => (
                      <option key={k} value={k}>
                        {THEME_PRESETS[k].name ?? k}
                      </option>
                    ))}
                  </select>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <FieldColor
                      label="Accent"
                      value={overrides.accentColor ?? THEME_PRESETS[presetKey].accentColor}
                      onChange={(v) => setOverrides((o) => ({ ...o, accentColor: v }))}
                    />
                    <FieldColor
                      label="Text"
                      value={overrides.textColor ?? THEME_PRESETS[presetKey].textColor}
                      onChange={(v) => setOverrides((o) => ({ ...o, textColor: v }))}
                    />
                    <FieldText
                      className="col-span-2"
                      label="Card BG (rgba)"
                      value={overrides.cardBg ?? THEME_PRESETS[presetKey].cardBg}
                      placeholder="rgba(255,255,255,0.10)"
                      onChange={(v) => setOverrides((o) => ({ ...o, cardBg: v }))}
                    />
                    <FieldText
                      label="Aurora Hue 1 (HSL)"
                      value={overrides.bg1 ?? THEME_PRESETS[presetKey].bg1}
                      placeholder="198 90% 40%"
                      onChange={(v) => setOverrides((o) => ({ ...o, bg1: v }))}
                    />
                    <FieldText
                      label="Aurora Hue 2 (HSL)"
                      value={overrides.bg2 ?? THEME_PRESETS[presetKey].bg2}
                      placeholder="187 76% 35%"
                      onChange={(v) => setOverrides((o) => ({ ...o, bg2: v }))}
                    />
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={saveTheme}
                      disabled={saving}
                      className="rounded-lg px-3 py-2"
                      style={{ background: "var(--accent)", color: "#000" }}
                    >
                      {saving ? "Saving..." : "Save theme"}
                    </button>
                    <button
                      onClick={() => {
                        // reset to current conversation theme (discard edits)
                        setPresetKey(conversation.themePresetKey ?? "ocean-turtle");
                        setOverrides(conversation.themeOverrides ?? {});
                        setPanelOpen(false);
                      }}
                      className="rounded-lg px-3 py-2"
                      style={{
                        border: `1px solid var(--accent)`,
                        color: "var(--text-color)",
                        background: "transparent",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div
                    className="rounded-xl p-4"
                    style={{ background: "var(--card-bg)", border: `1px solid var(--accent)` }}
                  >
                    <div
                      className="text-sm font-semibold mb-2"
                      style={{ color: "var(--text-color)" }}
                    >
                      Preview
                    </div>
                    <div className="space-y-2">
                      <Bubble who="eagle" text="Theme changes preview live." />
                      <Bubble who="you" text="Looks great!" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </ThemeScope>
  );
}

/** helpers **/
function renderSender(userId: string, people: Participant[], currentUserId?: string, currentUserName?: string) {
  const p = people.find((x) => x.id === userId);
  if (p) {
    return p.displayName || p.username;
  }
  // If sender not found in participants, check if it's the current user
  if (userId === currentUserId) {
    return currentUserName || "You";
  }
  return userId.slice(0, 6);
}

function FieldColor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-xs" style={{ color: "var(--text-color)" }}>
      {label}
      <input
        type="color"
        className="block mt-1"
        value={toHex(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
function FieldText({
  label,
  value,
  onChange,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <label
      className={`block text-xs ${className ?? ""}`}
      style={{ color: "var(--text-color)" }}
    >
      {label}
      <input
        className="block w-full mt-1 rounded px-2 py-1 bg-transparent"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ color: "var(--text-color)", border: `1px solid var(--accent)` }}
      />
    </label>
  );
}
function Bubble({ who, text }: { who: string; text: string }) {
  return (
    <div
      className="rounded-lg p-2 text-sm"
      style={{
        background: "var(--card-bg)",
        border: `1px solid var(--accent)`,
        color: "var(--text-color)",
      }}
    >
      <span className="opacity-80">{who}: </span>
      {text}
    </div>
  );
}
function toHex(c: string) {
  if (c.startsWith("#")) return c;
  try {
    const ctx = document.createElement("canvas").getContext("2d")!;
    ctx.fillStyle = c;
    return ctx.fillStyle as string;
  } catch {
    return "#ffffff";
  }
}
